// /login — password login via POST /auth/web-login (cookie flavor). If the
// account has TOTP enrolled the server answers 401 {code: "TOTP_REQUIRED"};
// the form then reveals a code field and retries (control-plane spec §REST).
//
// First-touch surface: it wears the cairn identity (the stacked mark over a
// faint topographic contour field — a cairn on a summit) so a new user lands
// somewhere that reads as trustworthy, not a bare form.

import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiFailure, webLogin } from "../api";
import { useApi } from "../session";
import { CairnMark } from "@/components/cairn-mark";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

export function LoginPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await webLogin(api, {
        email,
        password,
        ...(needTotp && totp !== "" ? { totp_code: totp } : {}),
      });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiFailure && err.code === "TOTP_REQUIRED") {
        // First refusal reveals the field; a wrong code keeps it visible.
        setError(needTotp ? "Invalid code — try again." : null);
        setNeedTotp(true);
      } else if (err instanceof ApiFailure) {
        setError(err.message);
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative grid min-h-svh place-items-center overflow-hidden p-6">
      <ContourField />
      <Card className="relative w-full max-w-sm">
        <CardContent className="flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <CairnMark className="size-9 text-primary" title="Cairn" />
            <span className="text-xl font-bold tracking-tight">cairn</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
              Self-hosted vault sync
            </span>
            <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          </div>

          <form onSubmit={submit}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="login-email">Email</FieldLabel>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="login-password">Password</FieldLabel>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
              {needTotp && (
                <Field>
                  <FieldLabel htmlFor="login-totp">Two-factor code</FieldLabel>
                  <Input
                    id="login-totp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totp}
                    onChange={(e) => setTotp(e.target.value)}
                    required
                  />
                </Field>
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={busy}>
                {busy && <Spinner />}
                {busy ? "Signing in…" : needTotp ? "Verify" : "Sign in"}
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}

/** Decorative topographic contour rings behind the auth card. */
function ContourField() {
  return (
    <svg
      className="contour-field"
      viewBox="0 0 600 600"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      {[40, 92, 150, 214, 284].map((r, i) => (
        <ellipse
          key={r}
          cx="300"
          cy="300"
          rx={r}
          ry={r * 0.82}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          opacity={1 - i * 0.14}
        />
      ))}
    </svg>
  );
}
